import React from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import { SignInViewProps } from "../auth.types";
import LogoSquare from "../../../assets/logoSquare";
import PasswordInput from "./PasswordInput";

const BackgroundGradient = () => (
  <Svg style={StyleSheet.absoluteFillObject} width="100%" height="100%">
    <Defs>
      <LinearGradient id="signInMobileBg" x1="0" y1="0" x2="0" y2="1">
        <Stop offset="0%" stopColor="#FCF2DC" />
        <Stop offset="100%" stopColor="#F3FEFF" />
      </LinearGradient>
    </Defs>
    <Rect width="100%" height="100%" fill="url(#signInMobileBg)" />
  </Svg>
);

export default function SignInViewMobile({
  email,
  password,
  error,
  isSigningIn,
  canSignIn,
  onEmailChange,
  onPasswordChange,
  onSubmitSignIn,
  onSwitchToSignUp,
  onSwitchToForgotPassword,
}: SignInViewProps) {
  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <BackgroundGradient />
      <KeyboardAvoidingView
        style={styles.keyboardAvoider}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <View style={styles.logoWrap}>
                <LogoSquare width={91} height={113} />
              </View>
              <Text style={styles.title}>
                You&#39;re in a safe place to practice meaningful conversations.
              </Text>
              <Text style={styles.subtitle}>
                In KNEXO, conversations are guided, time-limited, and designed
                to feel respectful and comfortable.
              </Text>

              <TextInput
                style={[styles.input, styles.inputAccent]}
                placeholder="Email"
                placeholderTextColor="#9AA0A6"
                autoCapitalize="none"
                keyboardType="email-address"
                value={email}
                onChangeText={onEmailChange}
              />

              <PasswordInput
                style={styles.input}
                placeholder="Password"
                placeholderTextColor="#9AA0A6"
                iconColor="#9AA0A6"
                value={password}
                onChangeText={onPasswordChange}
              />

              {error ? <Text style={styles.errorText}>{error}</Text> : null}

              <TouchableOpacity
                onPress={onSwitchToForgotPassword}
                disabled={!onSwitchToForgotPassword}
                style={styles.linkButton}
              >
                <Text style={styles.linkText}>Forgot Password</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={onSubmitSignIn}
                disabled={!canSignIn || isSigningIn}
                style={[
                  styles.primaryButton,
                  (!canSignIn || isSigningIn) && styles.primaryButtonDisabled,
                ]}
              >
                {isSigningIn ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>Enter KNEXO</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                onPress={onSwitchToSignUp}
                disabled={!onSwitchToSignUp}
                style={styles.switchButton}
              >
                <Text style={styles.switchText}>
                  New here?{" "}
                  <Text style={styles.switchLink}>Create a free account?</Text>
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  keyboardAvoider: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  card: {
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 28,
    shadowColor: "#000",
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 4,
  },
  cardContent: {
    alignItems: "center",
    gap: 20,
  },
  logoWrap: {
    alignItems: "center",
  },
  title: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: "700",
    color: "#2B2B2F",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 17,
    lineHeight: 22,
    color: "#2B2B2F",
    textAlign: "center",
  },
  input: {
    height: 69,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    borderRadius: 16,
    paddingHorizontal: 24,
    fontSize: 16,
    color: "#2B2B2F",
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  inputAccent: {
    borderColor: "#7EADEB",
  },
  errorText: {
    color: "#D32F2F",
    fontSize: 12,
  },
  linkButton: {
    alignSelf: "center",
    paddingVertical: 2,
  },
  linkText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#3A6FB1",
  },
  primaryButton: {
    width: "100%",
    height: 62,
    borderRadius: 16,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#FFFFFF",
  },
  switchButton: {
    alignItems: "center",
  },
  switchText: {
    fontSize: 16,
    lineHeight: 22,
    color: "#2B2B2F",
    textAlign: "center",
  },
  switchLink: {
    color: "#3A6FB1",
    fontWeight: "700",
  },
});
